import { formatReceiptMoney, formatReceiptWhen, escapeReceiptHtml } from './format';
import { encodeEscPosChars, ESC_POS_CUT, ESC_POS_PROLOGUE } from './escposCharset';
import { escposQrBlock } from './escposQr';
import {
  receiptItemBlock,
  receiptItemExtrasBlock,
  receiptTotalLine,
  receiptColsForBrowser,
  receiptCenterLineOnPaper,
  receiptLeftLine,
  receiptPlainTextLines,
  receiptSeparatorLine,
  receiptColsForEscPos,
} from './receiptPlainLayout';
import { wrapThermalPlainDocumentHtml } from './renderThermalHtml';
import type { ThermalPlainLineRole } from './receiptTypography';
import {
  pickupTrackingQrHtml,
  pickupTrackingQrLabel,
  pickupTrackingUrl,
} from './receiptPickupTracking';
import type { SaleReceiptDocument, SaleReceiptPayload } from './types';

function saleTrackingUrl(payload: SaleReceiptPayload): string | null {
  return pickupTrackingUrl(payload.trackingUrl);
}

function encoder(): { bytes: number[]; push: (...vals: number[]) => void; text: (s: string) => void } {
  const bytes: number[] = [];
  return {
    bytes,
    push(...vals) {
      bytes.push(...vals);
    },
    text(s: string) {
      bytes.push(...encodeEscPosChars(s));
    },
  };
}

function receiptPayments(payload: SaleReceiptPayload) {
  return payload.payments.filter((p) => p.amount > 0);
}

function receiptRefLine(payload: SaleReceiptPayload): string {
  const orderRef = payload.orderRefLabel?.trim();
  if (orderRef) return orderRef;
  return payload.saleRef ? `Ref. ${payload.saleRef}` : 'Ref. —';
}

function receiptFooter(payload: SaleReceiptPayload): string {
  return payload.footerMessage?.trim() || 'Gracias por tu compra';
}

function guestNameLine(payload: SaleReceiptPayload): string | null {
  const name = payload.pickupName?.trim();
  if (!name) return null;
  if (payload.fulfillmentLabel?.trim() === 'Comer acá') {
    return `A nombre de: ${name}`;
  }
  return `Retiro: ${name}`;
}

function saleTrackingQrLabel(payload: SaleReceiptPayload): string {
  return pickupTrackingQrLabel(payload.fulfillmentLabel);
}

function receiptClosingMessage(payload: SaleReceiptPayload): string | null {
  if (saleTrackingUrl(payload)) return null;
  return receiptFooter(payload);
}

function pushHeader(
  lines: Array<{ text: string; role: ThermalPlainLineRole }>,
  text: string | null | undefined,
  role: ThermalPlainLineRole,
  cols: number,
) {
  const t = text?.trim();
  if (!t) return;
  lines.push({ text: t.slice(0, cols), role });
}

/** Nombre comercial primero; sucursal solo si es distinta (multi-local). */
/** Cantidad de renglones de producto (≥2 para no ruido en venta de un ítem). */
function saleReceiptArticlesLine(payload: SaleReceiptPayload): string | null {
  const n = payload.lines.length;
  if (n < 2) return null;
  return `Artículos: ${n}`;
}

function saleReceiptBrandLines(payload: SaleReceiptPayload): string[] {
  const commercial = payload.organizationName?.trim();
  const branch = payload.branchName.trim();
  if (commercial) {
    const out = [commercial];
    if (branch.toLocaleLowerCase('es-AR') !== commercial.toLocaleLowerCase('es-AR')) {
      out.push(branch);
    }
    return out;
  }
  return branch ? [branch] : [];
}

/** GS ! — doble altura (reset inmediato después). */
const ESC_POS_DOUBLE_HEIGHT = [0x1d, 0x21, 0x01] as const;
const ESC_POS_NORMAL_SIZE = [0x1d, 0x21, 0x00] as const;

/** ESC/POS básico para ticket de venta (CP850; acentos/ñ mapeados). */
export function renderSaleReceiptEscPos(payload: SaleReceiptPayload): Uint8Array {
  const out = encoder();
  out.push(...ESC_POS_PROLOGUE);
  const widthMm = payload.widthMm ?? 72;
  const cols = receiptColsForEscPos(widthMm);
  const emitLeft = (line: string) => {
    out.text(`${receiptLeftLine(line, cols)}\n`);
  };
  const emitCenter = (line: string) => {
    out.text(`${receiptCenterLineOnPaper(line, cols, cols)}\n`);
  };
  const emitCenterWrapped = (text: string) => {
    for (const line of receiptPlainTextLines(text, cols)) emitCenter(line);
  };
  const emitTotal = (line: string) => {
    out.push(...ESC_POS_DOUBLE_HEIGHT);
    emitLeft(line);
    out.push(...ESC_POS_NORMAL_SIZE);
  };
  emitCenter('TICKET DE VENTA');
  if (payload.voided) emitCenter('*** ANULADA ***');
  const brandLines = saleReceiptBrandLines(payload);
  if (brandLines[0]) emitLeft(brandLines[0]);
  if (brandLines[1]) emitLeft(brandLines[1]);
  emitLeft(formatReceiptWhen(payload.createdAt));
  emitLeft(receiptRefLine(payload));
  if (payload.fulfillmentLabel?.trim()) emitLeft(payload.fulfillmentLabel.trim());
  const guestLine = guestNameLine(payload);
  if (guestLine) emitLeft(guestLine);
  if (payload.customerName?.trim()) emitLeft(`Cliente: ${payload.customerName.trim()}`);
  if (payload.diningTableName?.trim()) emitLeft(`Mesa: ${payload.diningTableName.trim()}`);
  emitLeft(receiptSeparatorLine(cols));
  for (const line of payload.lines) {
    const left = `${line.qty}x ${line.name}`;
    const block = receiptItemBlock(left, formatReceiptMoney(line.lineTotal), cols, { escPos: true });
    for (const row of block) emitLeft(row);
    for (const extra of receiptItemExtrasBlock(
      line.optionsLabel?.trim() || null,
      line.lineNote?.trim() || null,
      cols,
    )) {
      emitLeft(extra);
    }
  }
  emitLeft(receiptSeparatorLine(cols));
  emitLeft(`Subtotal: ${formatReceiptMoney(payload.subtotal)}`);
  const discount = Math.max(0, payload.discountTotal ?? 0);
  if (discount > 0) emitLeft(`Descuento: -${formatReceiptMoney(discount)}`);
  const tip = Math.max(0, Number(payload.tipAmount ?? 0) || 0);
  if (tip > 0) emitLeft(`Propina: ${formatReceiptMoney(tip)}`);
  emitTotal(`Total: ${formatReceiptMoney(payload.total)}`);
  const articlesLine = saleReceiptArticlesLine(payload);
  if (articlesLine) emitLeft(articlesLine);
  for (const p of receiptPayments(payload)) {
    emitLeft(`${p.methodLabel}: ${formatReceiptMoney(p.amount)}`);
  }
  if (payload.changeAmount != null && payload.changeAmount > 0) {
    emitLeft(`Vuelto: ${formatReceiptMoney(payload.changeAmount)}`);
  }
  const tracking = saleTrackingUrl(payload);
  if (tracking) {
    out.push(...escposQrBlock(tracking));
    emitCenterWrapped(saleTrackingQrLabel(payload));
  }
  const closing = receiptClosingMessage(payload);
  if (closing) emitCenterWrapped(closing);
  for (const note of payload.footerNotes ?? []) {
    if (note?.trim()) emitCenterWrapped(note.trim());
  }
  out.push(...ESC_POS_CUT);
  return Uint8Array.from(out.bytes);
}

export function buildSaleReceipt(payload: SaleReceiptPayload): SaleReceiptDocument {
  const widthMm = payload.widthMm ?? 72;
  const cols = receiptColsForBrowser(widthMm);
  const sep = receiptSeparatorLine(cols);
  const when = formatReceiptWhen(payload.createdAt);
  const discount = Math.max(0, payload.discountTotal ?? 0);
  const tip = Math.max(0, Number(payload.tipAmount ?? 0) || 0);
  const title = payload.voided ? 'TICKET (ANULADA)' : 'TICKET DE VENTA';

  const headerLines: Array<{ text: string; role: ThermalPlainLineRole }> = [];
  pushHeader(headerLines, title, 'title', cols);
  if (payload.voided) pushHeader(headerLines, 'ANULADA', 'void', cols);
  const brandLines = saleReceiptBrandLines(payload);
  pushHeader(headerLines, brandLines[0], 'branch', cols);
  pushHeader(headerLines, brandLines[1], 'subtitle', cols);
  pushHeader(headerLines, when, 'meta', cols);
  pushHeader(headerLines, receiptRefLine(payload), 'meta', cols);
  pushHeader(headerLines, payload.fulfillmentLabel, 'meta', cols);
  pushHeader(headerLines, guestNameLine(payload), 'meta', cols);
  pushHeader(headerLines, payload.customerName ? `Cliente: ${payload.customerName}` : null, 'meta', cols);
  pushHeader(
    headerLines,
    payload.diningTableName ? `Mesa: ${payload.diningTableName}` : null,
    'meta',
    cols,
  );
  pushHeader(headerLines, sep, 'sep', cols);

  const itemLines: string[] = payload.lines.flatMap((line) => {
    const left = `${line.qty}× ${line.name}`;
    const right = formatReceiptMoney(line.lineTotal);
    const opts = line.optionsLabel?.trim() || '';
    const note = line.lineNote?.trim() || '';
    return [
      ...receiptItemBlock(left, right, cols),
      ...receiptItemExtrasBlock(opts || null, note || null, cols),
    ];
  });

  const footerLines: Array<{ text: string; role: ThermalPlainLineRole }> = [
    { text: sep, role: 'sep' },
    {
      text: receiptTotalLine('Subtotal', formatReceiptMoney(payload.subtotal), cols),
      role: 'subtotal',
    },
  ];
  if (discount > 0) {
    footerLines.push({
      text: receiptTotalLine('Descuento', `-${formatReceiptMoney(discount)}`, cols),
      role: 'subtotal',
    });
  }
  if (tip > 0) {
    footerLines.push({
      text: receiptTotalLine('Propina', formatReceiptMoney(tip), cols),
      role: 'subtotal',
    });
  }
  footerLines.push({
    text: receiptTotalLine('Total', formatReceiptMoney(payload.total), cols),
    role: 'total',
  });
  const articlesLine = saleReceiptArticlesLine(payload);
  if (articlesLine) {
    footerLines.push({ text: articlesLine, role: 'meta' });
  }
  for (const p of receiptPayments(payload)) {
    footerLines.push({
      text: receiptTotalLine(p.methodLabel, formatReceiptMoney(p.amount), cols),
      role: 'payment',
    });
  }
  if (payload.changeAmount != null && payload.changeAmount > 0) {
    footerLines.push({
      text: receiptTotalLine('Vuelto', formatReceiptMoney(payload.changeAmount), cols),
      role: 'payment',
    });
  }
  const tracking = saleTrackingUrl(payload);
  if (tracking) {
    for (const line of receiptPlainTextLines(saleTrackingQrLabel(payload), cols)) {
      footerLines.push({ text: line, role: 'foot' });
    }
  }
  const closing = receiptClosingMessage(payload);
  if (closing) {
    for (const line of receiptPlainTextLines(closing, cols)) {
      footerLines.push({ text: line, role: 'foot' });
    }
  }
  for (const note of payload.footerNotes ?? []) {
    if (!note?.trim()) continue;
    for (const line of receiptPlainTextLines(note, cols)) {
      footerLines.push({ text: line, role: 'foot' });
    }
  }

  const textParts = [
    title,
    payload.voided ? '*** ANULADA ***' : null,
    ...saleReceiptBrandLines(payload),
    when || null,
    receiptRefLine(payload),
    payload.fulfillmentLabel?.trim() || null,
    guestNameLine(payload),
    payload.customerName ? `Cliente: ${payload.customerName}` : null,
    payload.diningTableName ? `Mesa: ${payload.diningTableName}` : null,
    sep,
    ...itemLines,
    ...footerLines.map((l) => l.text),
  ];
  const text = textParts.filter((l): l is string => l != null && l !== '').join('\n');

  const appendHtml = tracking
    ? pickupTrackingQrHtml(tracking, saleTrackingQrLabel(payload))
    : undefined;

  const html = wrapThermalPlainDocumentHtml({
    widthMm,
    headerLines,
    itemsPre: itemLines.join('\n'),
    footerLines,
    appendHtml,
  });

  return {
    text,
    html,
    escpos: renderSaleReceiptEscPos(payload),
  };
}
